import { useCallback, useEffect, useRef, useState } from 'react';
import { SCHEMA_VERSION } from '../data/db';
import { report } from '../sentry';
import type { AttemptRow, RevealRow, SessionRow, StateRow } from '../data/db';
import {
  loadAttemptViews,
  loadDevice,
  loadRevealViews,
  loadState,
  saveDevice,
  saveSession,
  saveState,
  seqFloor,
} from '../data/repository';
import type { Tile } from '../engine/facts';
import { ALL_TILES, poolFor } from '../engine/facts';
import { tick } from '../engine/hlc';
import type { Hlc } from '../engine/hlc';
import { masteryDelta } from '../engine/mastery';
import type { MasteryDelta } from '../engine/mastery';
import type { AttemptView } from '../engine/mastery';
import { mulberry32 } from '../engine/rng';
import { advance, currentTask, initQueue, progress, submit } from '../engine/queue';
import type { QueueState } from '../engine/queue';
import { buildRepeatSession } from '../engine/repeat';
import { buildSession } from '../engine/session';
import type { Task } from '../engine/session';
import type { Mode } from '../engine/exercises/compare';
import { sessionTotals } from '../engine/stats';
import type { SessionTotals } from '../engine/stats';

export interface Summary {
  /** Идентификатор завершённой сессии — он же сегмент маршрута итогов. */
  sessionId: string;
  /** Удалось ли посчитать прирост освоенности. Пустые `gained` и `lost`
   *  при `false` означают «неизвестно», а не «не изменилось». */
  deltaKnown: boolean;
  totals: SessionTotals;
  wallClockMs: number;
  gained: string[];
  lost: string[];
  erroredTaskIds: string[];
}

interface Device {
  /** Профиль, которому принадлежит всё, что пишется в этой сессии. Держится
   *  рядом с устройством, потому что берётся один раз при монтировании: смена
   *  профиля обязана перемонтировать провайдер, а не переписать ссылку. */
  profileId: string;
  deviceId: string;
  hlc: Hlc | null;
  dealerCursors: StateRow['dealerCursors'];
  storagePersisted: boolean | null;
}

const ALL_FACT_IDS = [
  ...poolFor(ALL_TILES, 'mul').map((f) => f.id),
  ...poolFor(ALL_TILES, 'div').map((f) => f.id),
];

function toView(row: AttemptRow, order: number): AttemptView {
  return {
    factIds: row.factIds,
    exerciseType: row.exerciseType,
    isCorrect: row.isCorrect,
    answerMs: row.answerMs,
    resumedAfterBackground: row.resumedAfterBackground,
    atMs: row.presentedAt,
    order,
  };
}

/** Профиль приходит доводом, а потому меняется только перемонтированием.
 *
 *  Состояние читается ОДИН раз в эффекте с пустыми зависимостями и живёт в
 *  ссылке. Смени профиль без перемонтирования — и в памяти останутся
 *  `dealerCursors` предыдущего ребёнка, а первый же запуск сессии запишет их
 *  под ключ нового: очередь раздачи одного съест выдачи другого, молча.
 *  Поэтому провайдер монтируется с `key={profileId}` (см. `ProfileGate`), а
 *  не получает новый довод в тот же экземпляр. */
export function useTrainer(profileId: string) {
  const [queue, setQueue] = useState<QueueState | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [ready, setReady] = useState(false);
  /** Не удалось прочитать состояние устройства. Без этого признака отказ
   *  IndexedDB оставлял бы приложение в «Загрузка…» навсегда, без единой
   *  кнопки на экране. */
  const [initFailed, setInitFailed] = useState(false);
  /** Запись сессии не удалась. Без этого признака экран тренировки ждал бы
   *  сводку, которая уже никогда не придёт, и выйти было бы нечем. */
  const [finishFailed, setFinishFailed] = useState(false);

  const queueRef = useRef<QueueState | null>(null);
  const device = useRef<Device | null>(null);
  const attempts = useRef<AttemptRow[]>([]);
  const reveals = useRef<RevealRow[]>([]);
  const sessionId = useRef<string>('');
  /** Задания идущей сессии. Держим их, потому что `buildRepeatSession`
   *  переносит задание целиком — с типом и вариантами, — а журнал хранит
   *  попытки, а не задания. Отсюда и граница: повторить ошибки можно, пока
   *  открыты итоги, но не из истории. */
  const tasks = useRef<Task[]>([]);
  const sourceSessionId = useRef<string | null>(null);
  const startedAt = useRef(0);
  const activeMs = useRef(0);
  const activeSince = useRef(0);
  const askedAt = useRef(0);
  const interrupted = useRef(false);
  const config = useRef<{ tiles: Tile[]; mode: Mode }>({ tiles: [], mode: 'mul' });

  /** Первый запуск: заводим устройство и профиль и просим постоянное
   *  хранилище.
   *
   *  Признак постоянного хранилища записывается сразу, а не при первой
   *  сессии: пока он лежал только в памяти до `saveState`, устройство, на
   *  котором тренировку ни разу не довели до конца, спрашивало разрешение
   *  заново при каждом запуске. */
  useEffect(() => {
    void (async () => {
      try {
        const stored = await loadDevice();
        /** `ensureActiveProfile` обязан завести строку устройства. Если её
         *  нет, дальше пришлось бы придумывать `deviceId` на месте — и два
         *  устройства могли бы разойтись молча. */
        if (!stored) throw new Error('строка устройства не создана');
        const state = await loadState(profileId);
        let persisted = stored?.storagePersisted ?? null;
        if (persisted === null && navigator.storage?.persist) {
          persisted = await navigator.storage.persist().catch(() => false);
          await saveDevice({
            deviceId: stored.deviceId,
            storagePersisted: persisted,
            activeProfileId: profileId,
            schemaVersion: SCHEMA_VERSION,
            /** Владельца несём из прочитанной строки, а не подставляем
             *  `null`: `saveDevice` перезаписывает строку целиком, и
             *  забытое здесь поле стёрло бы отметку о том, чьи это данные.
             *  Тип нарочно требует все поля — именно чтобы это забывалось
             *  на глазах компилятора, а не молча. */
            ownerId: stored.ownerId,
          });
        }
        /** Часы потеряны, а журнал цел — так бывает при вытеснении
         *  хранилища и при наполовину прошедшем обновлении. Начинать
         *  заново от текущего времени нельзя: отставшие стенные часы дадут
         *  отметки ниже курсора отправки, и такие записи не уедут никогда.
         *  Подробности — в `seqFloor`. */
        const floor = state?.hlc ?? (await seqFloor(profileId));
        device.current = {
          profileId,
          deviceId: stored.deviceId,
          hlc: floor === null ? null : { ...floor, deviceId: stored.deviceId },
          dealerCursors: state?.dealerCursors ?? {},
          storagePersisted: persisted,
        };
        setReady(true);
      } catch (error) {
        report(error, 'loadState');
        setInitFailed(true);
      }
    })();
    // Профиль в зависимостях не нужен и был бы опасен: он не меняется без
    // перемонтирования, а эффект с ним в списке создал бы вторую, тихо
    // расходящуюся дорогу смены профиля.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const training = queue !== null && queue.phase !== 'done';

  /** Время сессии копится только пока вкладка на экране (раздел 6.1). */
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (activeSince.current) activeMs.current += performance.now() - activeSince.current;
        activeSince.current = 0;
        /** Помечается только задание, которое В ЭТОТ МОМЕНТ ждало ответа
         *  (раздел 5.8). Уход в фон во время показа обратной связи к
         *  следующему заданию отношения не имеет: то показывается уже как
         *  новое. Пока отметка ставилась на любой уход, ребёнок, глянувший
         *  уведомление между заданиями, терял годный замер по чужому
         *  факту — и «Освоено N из 180» оказывалось меньше заработанного. */
        if (training && queueRef.current?.phase === 'awaiting') interrupted.current = true;
      } else {
        activeSince.current = performance.now();
        askedAt.current = performance.now();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [training]);

  const nextSeq = useCallback((): Hlc => {
    const d = device.current!;
    const seq = tick(d.hlc, Date.now(), d.deviceId);
    d.hlc = seq;
    return seq;
  }, []);

  /** Общее начало обычной сессии и сессии повторения. Всё, что отличается,
   *  вызывающая сторона решает до вызова: сами задания и родительскую
   *  сессию. */
  const begin = useCallback(
    (built: Task[], tiles: Tile[], mode: Mode, source: string | null) => {
      config.current = { tiles, mode };
      tasks.current = built;
      sourceSessionId.current = source;
      sessionId.current = crypto.randomUUID();
      attempts.current = [];
      reveals.current = [];
      startedAt.current = Date.now();
      activeMs.current = 0;
      activeSince.current = performance.now();
      askedAt.current = performance.now();
      interrupted.current = false;
      const fresh = initQueue(built);
      queueRef.current = fresh;
      setQueue(fresh);
      setSummary(null);
      setFinishFailed(false);
    },
    [],
  );

  const start = useCallback(
    (tiles: Tile[], mode: Mode) => {
      const d = device.current!;
      const built = buildSession({
        tiles,
        mode,
        taskCount: 20,
        dealer: d.dealerCursors,
        rng: mulberry32(Date.now() >>> 0),
        makeId: () => crypto.randomUUID(),
      });
      d.dealerCursors = built.dealer;
      void saveState(d.profileId, {
        hlc: d.hlc,
        dealerCursors: d.dealerCursors,
        schemaVersion: SCHEMA_VERSION,
      });
      begin(built.tasks, tiles, mode, null);
    },
    [begin],
  );

  /** Повторение ошибок (раздел 5.9). Курсор раздатчика не трогается: задания
   *  переносятся из уже сыгранной сессии, новых выдач нет. */
  const startRepeat = useCallback(() => {
    const done = summary;
    if (!done || done.erroredTaskIds.length === 0) return;
    const repeat = buildRepeatSession({
      tasks: tasks.current,
      erroredTaskIds: done.erroredTaskIds,
      rng: mulberry32(Date.now() >>> 0),
      makeId: () => crypto.randomUUID(),
    });
    begin(repeat, config.current.tiles, config.current.mode, done.sessionId);
  }, [begin, summary]);

  const finish = useCallback(async (state: QueueState) => {
    const d = device.current!;
    if (activeSince.current) activeMs.current += performance.now() - activeSince.current;
    activeSince.current = performance.now();

    /** Снимок буферов до первого ожидания. Дальше идут два чтения из
     *  IndexedDB, и в это окно помещается целая новая тренировка: диалог
     *  «Тренировка уже сохраняется…» → «Выйти и потерять» → «Начать»
     *  обнуляет `attempts.current` через `begin`. Пока запись читала
     *  ссылку, а не снимок, в истории появлялась строка «20 заданий», к
     *  которой не прилагалось НИ ОДНОЙ попытки: освоенность считается по
     *  журналу, а журнала не было. */
    const attemptRows = attempts.current;
    const revealRows = reveals.current;

    const totals = sessionTotals(
      attemptRows.map((row, i) => toView(row, i)),
      state.taskCount,
      state.assisted,
    );
    const row: SessionRow = {
      id: sessionId.current,
      profileId: d.profileId,
      startedAt: startedAt.current,
      finishedAt: Date.now(),
      wallClockMs: Math.round(activeMs.current),
      tiles: config.current.tiles,
      mode: config.current.mode,
      taskCount: state.taskCount,
      totalAttempts: totals.totalAttempts,
      correctAttempts: totals.correctAttempts,
      wrongAttempts: totals.wrongAttempts,
      assistedTasks: totals.assistedTasks,
      accuracy: totals.accuracy,
      totalAnswerMs: totals.totalAnswerMs,
      avgAnswerMs: totals.avgAnswerMs,
      avgCorrectAnswerMs: totals.avgCorrectAnswerMs,
      sourceSessionId: sourceSessionId.current,
      /** Третий вызов `nextSeq` ничего не стоит, а без него оба составных
       *  индекса слепы и сессия не уезжает на сервер никогда. */
      seq: nextSeq(),
      deviceId: d.deviceId,
      schemaVersion: SCHEMA_VERSION,
    };

    /** Журнал читается ДО записи: `before` обязан быть состоянием без
     *  попыток этой сессии, иначе прирост освоенности посчитается по
     *  журналу, где они уже есть.
     *
     *  Но отказ чтения не должен стоить ребёнку пройденной тренировки.
     *  Пока чтение и запись стояли одной цепочкой, отклонение первого
     *  роняло вторую: двадцать решённых заданий пропадали безвозвратно, а
     *  экран сообщал «записать не вышло» — хотя запись прошла бы. Поэтому
     *  дельта необязательна, а запись — нет. */
    let delta: MasteryDelta = { gained: [], lost: [] };
    /** Посчитан ли прирост вообще. Пустые списки означают «ничего не
     *  сдвинулось», и путать это с «посчитать не вышло» нельзя: итоги
     *  сообщали бы «Освоенность не изменилась» после безупречной
     *  тренировки, хотя приложение просто не смогло сравнить. */
    let deltaKnown = true;
    try {
      const before = await loadAttemptViews(d.profileId);
      const after = [
        ...before,
        ...attemptRows.map((a, i) => toView(a, before.length + i)),
      ];
      const revealViews = [
        ...(await loadRevealViews(d.profileId)),
        ...revealRows.map((r) => ({ factId: r.factId, atMs: r.revealedAt })),
      ];
      delta = masteryDelta(ALL_FACT_IDS, before, after, revealViews);
    } catch (error) {
      report(error, 'masteryDelta');
      deltaKnown = false;
    }

    await saveSession({ session: row, attempts: attemptRows, reveals: revealRows });
    void saveState(d.profileId, {
      hlc: d.hlc,
      dealerCursors: d.dealerCursors,
      schemaVersion: SCHEMA_VERSION,
    });

    /** Пока шли два чтения журнала, ребёнок мог бросить эту сессию и начать
     *  новую. Записать её всё равно надо — она пройдена, — но показывать по
     *  ней итоги уже нельзя: сводка всплыла бы поверх живой тренировки, а
     *  вместе с ней и диалог выхода, которого никто не вызывал. */
    if (sessionId.current !== row.id) return;

    const errored = [...new Set(attemptRows.filter((a) => !a.isCorrect).map((a) => a.taskId))];
    setSummary({
      sessionId: row.id,
      deltaKnown,
      totals,
      wallClockMs: row.wallClockMs,
      gained: delta.gained,
      lost: delta.lost,
      erroredTaskIds: errored,
    });
  }, [nextSeq]);

  const answer = useCallback(
    (given: unknown, isCorrect: boolean) => {
      /** Побочные действия делаются здесь, а не внутри обновления состояния:
       *  React вызывает функции-обновители дважды в StrictMode, и запись
       *  попытки в буфер задваивалась бы. */
      const state = queueRef.current;
      if (!state || state.phase !== 'awaiting') return;
      const task = currentTask(state);
      if (!task) return;
      const d = device.current!;
      const answerMs = Math.max(0, Math.round(performance.now() - askedAt.current));
      /** Одна отметка на попытку и на её раскрытие. Раздельные вызовы
       *  `Date.now()` изредка попадают в разные миллисекунды, и тогда
       *  неверная попытка оказывается на миллисекунду РАНЬШЕ собственного
       *  раскрытия. Фильтр годности пропускает её как честный замер, она
       *  обрывает чистую серию, и факт теряет статус «Освоен» — при
       *  полностью одинаковой истории занятий. */
      const now = Date.now();
      attempts.current.push({
        id: crypto.randomUUID(),
        profileId: d.profileId,
        sessionId: sessionId.current,
        taskId: task.id,
        factIds: task.partner ? [task.fact.id, task.partner.id] : [task.fact.id],
        leftFactId: task.partner ? task.fact.id : null,
        exerciseType: task.type,
        presented: presentedOf(task),
        /** Умолчание недостижимо, и довод обязан быть полным — короткого
         *  «сборщик заполняет карту» мало, на это указало ревью.
         *
         *  Полностью: карта живёт только в состоянии React, в базу не
         *  попадает и ниоткуда не восстанавливается (в `AttemptRow` есть
         *  одноимённое ПОЛЕ, но это записанное число, а не карта).
         *  Заполняет её единственное место — `initQueue`, по списку тех же
         *  заданий, что потом и идут через очередь; `advance` только
         *  наращивает значение по уже существующему ключу. Ключи не
         *  меняются: это `task.id` из того же списка.
         *
         *  Оставлено, а не удалено: без умолчания пропавший ключ дал бы
         *  `undefined` и `NaN` в записанной строке журнала — молча и
         *  навсегда. */
        showIndex: state.showIndex[task.id] ?? 1,
        resumedAfterBackground: interrupted.current,
        answerMs,
        isCorrect,
        given,
        expected: expectedOf(task),
        ordinal: attempts.current.length + 1,
        seq: nextSeq(),
        deviceId: d.deviceId,
        schemaVersion: SCHEMA_VERSION,
        presentedAt: now,
      });
      interrupted.current = false;

      const updated = submit(state, isCorrect);
      for (const factId of updated.lastRevealed) {
        reveals.current.push({
          id: crypto.randomUUID(),
          profileId: d.profileId,
          factId,
          sessionId: sessionId.current,
          seq: nextSeq(),
          revealedAt: now,
          deviceId: d.deviceId,
          schemaVersion: SCHEMA_VERSION,
        });
      }
      queueRef.current = updated;
      setQueue(updated);
    },
    [nextSeq],
  );

  const next = useCallback(() => {
    const state = queueRef.current;
    if (!state) return;
    /** Очередь уже пуста. `advance` вернул бы новый объект с той же фазой, и
     *  запись сессии пошла бы во второй раз: `finish` читает журнал, куда
     *  первый заход уже сохранил попытки, и складывает их с буфером —
     *  попытки сессии оказались бы в расчёте освоенности дважды, а на
     *  итогах появились бы «освоенные» примеры, которых ребёнок не осваивал. */
    if (state.phase === 'done') return;
    const moved = advance(state);
    queueRef.current = moved;
    setQueue(moved);
    askedAt.current = performance.now();
    if (moved.phase === 'done') {
      void finish(moved).catch((error: unknown) => {
        setFinishFailed(true);
        /** Отправляем ЯВНО, а не повторным отклонением наружу.
         *
         *  Прежде здесь стоял `Promise.reject`, а комментарий обещал, что
         *  необработанное отклонение подхватит Sentry. Подхватило бы — но не
         *  `ErrorBoundary`, как сказано в `sentry.ts`: тот ловит только
         *  исключения отрисовки. Отклонения ловит интеграция глобальных
         *  обработчиков, включённая по УМОЛЧАНИЮ библиотеки и нигде у нас не
         *  объявленная.
         *
         *  Экран при этом обещает ребёнку «мы уже знаем об этой ошибке» —
         *  то есть обещание держалось на умолчании, которое никто не выбирал
         *  и которое молча переворачивается при обновлении библиотеки. Тот
         *  же довод, по которому в `sentry.ts` явно заданы три настройки
         *  маскировки: умолчание, на которое опираются молча, — это не
         *  решение, а надежда.
         *
         *  Повторного отклонения больше нет: рядом с явной отправкой оно
         *  давало бы второе событие об одной поломке. */
        report(error, 'finishSession');
      });
    }
  }, [finish]);

  /** Отсчёт времени ответа начинается заново.
   *
   *  Нужен, когда экран был заморожен не уходом в фон, а диалогом выхода:
   *  ответить в это время приложение само не давало, а часы шли. Случайное
   *  касание крестика, десять секунд на чтение диалога и «Продолжить
   *  тренировку» превращали ответ за 200 мс в замер 10,2 секунды при пороге
   *  беглости 3 — и факт уезжал из «Освоено» в «Разгонять». */
  const resumeTimer = useCallback(() => {
    askedAt.current = performance.now();
  }, []);

  /** Бросить состояние сессии. Навигацию делает вызывающая сторона: хук про
   *  маршруты ничего не знает. */
  const reset = useCallback(() => {
    queueRef.current = null;
    setQueue(null);
    setSummary(null);
  }, []);

  return {
    ready,
    profileId,
    initFailed,
    training,
    finishFailed,
    queue,
    summary,
    task: queue ? currentTask(queue) : null,
    progress: queue ? progress(queue) : 0,
    start,
    startRepeat,
    answer,
    next,
    resumeTimer,
    reset,
  };
}

function presentedOf(task: Task): unknown {
  if (task.type === 'choice4') return task.options;
  if (task.type === 'truefalse') return task.presented;
  return { left: task.fact.id, right: task.partner?.id };
}

function expectedOf(task: Task): unknown {
  if (task.type === 'choice4') return task.fact.answer;
  if (task.type === 'truefalse') return task.presented === task.fact.answer;
  return task.relation;
}
